import cornerstone from "cornerstone-core";
import * as instanceSource from "./instance-source";
import * as redactionStore from "../redaction/redaction-store";
import { recordDecodeFromSource } from "../session-stats";
// Circular ESM live binding with cornerstone-setup.js — safe to call at runtime.
import { noteFrameSize } from "./cache-budget";

/**
 * Image loader for the `dicomfile:` scheme.
 *
 * Every frame is decoded from its original file, every time it's needed. There is
 * no second copy of the pixels anywhere — which is the whole point: the image
 * cache can evict as aggressively as it likes and nothing is ever lost, so peak
 * memory is bounded by the cache limit rather than by the size of the study.
 *
 * `instance-source` keeps the parsed file hot across the frames of one instance,
 * so scrolling or exporting a multiframe cine reads and parses the file once, not
 * once per frame.
 *
 * Redaction is applied here rather than baked into stored pixels, so a re-decoded
 * frame comes back redacted without the caller knowing anything about it.
 */

// Matches maxWebWorkers in cornerstone-setup. A fast wheel-scroll fires many
// concurrent loads, and each one pins a whole parsed file while it decodes;
// without a limit, dozens of P10 buffers can be resident at once. Nothing nested
// awaits this, so it can't deadlock.
const MAX_CONCURRENT_DECODES = 4;
let active = 0;
const waiting: (() => void)[] = [];

async function withPermit<T>(fn: () => Promise<T>): Promise<T> {
  if (active >= MAX_CONCURRENT_DECODES) {
    await new Promise<void>((resolve) => waiting.push(resolve));
  }
  active += 1;
  try {
    return await fn();
  } finally {
    active -= 1;
    waiting.shift()?.();
  }
}

export default function dicomfileImageLoader(imageId: string) {
  const promise = withPermit(async () => {
    const url = instanceSource.urlFor(imageId);
    // `dicomfile-raw:` is registered for our own decode-only loader, which reads
    // the dataSet we're about to pin. See dicomfile-raw-loader.ts.
    const rawImageId = `dicomfile-raw:${imageId.slice("dicomfile:".length)}`;

    await instanceSource.acquire(url, imageId);
    let image: any;
    try {
      // cornerstone.loadImage (not loadAndCacheImage) — the raw id must not get
      // its own cache entry alongside the dicomfile: one.
      image = await cornerstone.loadImage(rawImageId);
    } finally {
      instanceSource.release(url);
    }

    // Callers see the `dicomfile:` id, not the internal raw one.
    image.imageId = imageId;
    // The decoded frame must not hold a reference to the parsed dataSet: that
    // would pin the whole source file in the image cache while `sizeInBytes`
    // reports only this frame, so the cache would believe it's under its limit
    // while the heap holds one entire file per cached frame.
    image.data = null;

    redactionStore.applyTo(image);
    // Keeps the cache ceiling above a single frame, so an unusually large frame
    // can't make the cache thrash itself empty on every load.
    noteFrameSize(image.sizeInBytes);
    recordDecodeFromSource();

    return image;
  });

  return { promise };
}
